/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { CopyOutlined } from '@ant-design/icons';
import { Modal, Space } from 'antd';

import { CreateOauthClientResponse2 } from '@/client';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { Input } from '@/primitives/Input';
import { Text } from '@/primitives/Text';
import { Title } from '@/primitives/Title';

type OAuthClientCreatedModalProps = {
  createdClient: CreateOauthClientResponse2;
  onClose: () => void;
  copyToClipboard: (text: string) => void;
};

export function OAuthClientCreatedModal({ createdClient, onClose, copyToClipboard }: OAuthClientCreatedModalProps) {
  const { t } = useMimicTranslation('oauthClients');
  const { t: globalT } = useMimicTranslation('global');

  return (
    <Modal
      open
      onCancel={onClose}
      onOk={onClose}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={t('createdModal.ok')}
      width={600}
    >
      <Container padding="sm">
        <Title level={4} data-testid="created-client-title">
          {createdClient.data.name}
        </Title>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Text style={{ fontWeight: '600' }}>{t('createdModal.clientIdLabel')}</Text>
            <Space.Compact>
              <Input readOnly value={createdClient.data.clientId} dataTestId="client-id-field" />
              <Button
                type="default"
                onClick={() => copyToClipboard(createdClient.data.clientId)}
                dataTestId="copy-client-id-button"
              >
                <CopyOutlined /> {globalT('copy')}
              </Button>
            </Space.Compact>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <Text style={{ fontWeight: '600' }}>{t('createdModal.clientSecretLabel')}</Text>
            <Space.Compact>
              <Input readOnly value={createdClient.data.clientSecret} dataTestId="client-secret-field" />
              <Button
                type="default"
                onClick={() => copyToClipboard(createdClient.data.clientSecret)}
                dataTestId="copy-client-secret-button"
              >
                <CopyOutlined /> {globalT('copy')}
              </Button>
            </Space.Compact>
          </div>
          <Text type="error">
            <strong>{t('createdModal.warningNote')}</strong> {t('createdModal.warning')}
          </Text>
        </div>
      </Container>
    </Modal>
  );
}
